import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu } from 'lucide-react';
import { api } from '@/lib/api';
import { navGroups, groupForRoute } from '@/features/owner/nav';
import { useSchool } from '@/lib/useSchool';
import { useAuth } from '@/stores/auth';
import { useQuery } from '@tanstack/react-query';
import { getSchoolProfile } from '@/api/owner';

/**
 * Owner shell: blue sidebar (#0A2540) + white surface.
 * Each group is a collapsible dropdown. Active group auto-expands.
 */
export function OwnerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  const profileQ = useQuery({
    queryKey: ['owner-profile'],
    queryFn: () => getSchoolProfile().then((r: any) => r.data?.data ?? r.data),
    staleTime: 5 * 60 * 1000,
  });
  // live counts for things waiting on the owner
  const dash = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => api.get('/school/dashboard').then((r) => r.data?.data), refetchInterval: 120_000, retry: false });
  const pend = dash.data?.pending ?? {};
  const live: Record<string, number | undefined> = { approvals: pend.money_requests, withdrawals: pend.withdrawals, expenses: pend.expenses, admissions: pend.applications, families: pend.parent_link_requests, 'held-results': pend.held_results };
  const brand = useSchool();
  const logoUrl = brand.logo ?? profileQ.data?.logo_url ?? null;
  const [logoBroken, setLogoBroken] = useState(false);
  const schoolName = brand.name || profileQ.data?.name || user?.schoolName || 'School';

  // Extract current page from URL — e.g. /owner/students/123 -> 'students'
  const match = location.pathname.match(/^\/owner\/([^/]+)/);
  const activePage = match?.[1] ?? 'dashboard';
  const activeGroup = groupForRoute(activePage);

  const [expanded, setExpanded] = useState<string | null>(activeGroup ?? 'overview');
  const [mobileOpen, setMobileOpen] = useState(false);

  // When user navigates, ensure the correct group is expanded
  useEffect(() => {
    if (activeGroup) setExpanded(activeGroup);
    setMobileOpen(false);
  }, [activeGroup]);

  const toggle = (key: string) => {
    setExpanded((cur) => (cur === key ? null : key));
  };

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.name ?? 'Owner')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">

      {/* ── SIDEBAR ── */}
      <aside
        className={`
          drawer-safe fixed md:static inset-y-0 left-0 z-40 w-64 bg-[#0A2540] text-slate-100
          flex flex-col transform transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}
      >
        {/* Brand */}
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          {logoUrl && !logoBroken ? (
            <img src={logoUrl} alt="" onError={() => setLogoBroken(true)} className="w-9 h-9 rounded-lg object-contain bg-white flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white font-extrabold flex-shrink-0">
              {schoolName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">
              {schoolName}
            </div>
            <div className="text-[11px] text-slate-400 truncate">
              School Owner
            </div>
          </div>
        </div>

        {/* Groups */}
        <nav className="flex-1 overflow-y-auto py-3">
          {navGroups.map((group) => {
            const isExpanded = expanded === group.key;
            const isActive = activeGroup === group.key;
            const Icon = group.icon;

            return (
              <div key={group.key} className="mb-1">
                <button
                  onClick={() => toggle(group.key)}
                  className={`
                    w-full flex items-center justify-between px-4 py-2.5 text-left text-sm font-semibold transition
                    ${isActive ? 'text-white bg-white/5 border-l-2 border-green-500' : 'text-slate-300 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}
                  `}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{group.section}</span>
                  </div>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isExpanded && (
                  <ul className="py-1">
                    {group.items.map((item) => {
                      const active = item.to.includes('?') ? `${activePage}${location.search}` === item.to : activePage === item.to && !(location.search && group.items.some((x) => x.to === `${activePage}${location.search}`));
                      return (
                        <li key={item.to}>
                          <Link
                            to={`/owner/${item.to}`}
                            className={`
                              flex items-center justify-between px-4 pl-11 py-2 text-[13px] transition
                              ${active
                                ? 'text-white bg-green-600/20 font-semibold border-l-2 border-green-500 -ml-[2px]'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'}
                            `}
                          >
                            <span>{item.label}</span>
                            {(live[item.to] ?? item.badge ?? 0) > 0 && (
                              <span
                                className={`
                                  text-[10px] font-bold px-1.5 py-0.5 rounded-full
                                  ${item.badgeTone === 'orange' ? 'bg-amber-500 text-white' :
                                    item.badgeTone === 'red' ? 'bg-red-500 text-white' :
                                    'bg-green-500 text-white'}
                                `}
                              >
                                {live[item.to] ?? item.badge}
                              </span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/10 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[10px] text-slate-400 truncate">School Owner</div>
          </div>
          <button
            onClick={signOut}
            className="text-slate-400 hover:text-white transition p-1"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden mobile-topbar bg-white border-b border-slate-200 px-2 flex items-center gap-1">
          <button
            onClick={() => setMobileOpen(true)}
            className="tap-target text-slate-700"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-bold text-slate-900 truncate">
            {schoolName}
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
